/**
 * HUD Layout Configuration
 * 
 * Centralized default positions, sizes, and styling for HUD elements.
 * Previously these were hardcoded throughout HUD.tsx
 */

/**
 * Panel positioning strategies
 */
export type PanelPosition = 
  | { type: 'absolute'; x: number; y: number }
  | { type: 'center'; y: number }
  | { type: 'bottom-left'; x: number; offset: number }
  | { type: 'bottom-right'; offset: number; y?: number }
  | { type: 'top-right'; x: number; y: number };

/**
 * Calculate actual X position from strategy
 */
export function calculateX(position: PanelPosition): number {
  switch (position.type) {
  case 'absolute':
    return position.x;
  case 'center':
    return typeof window !== 'undefined' ? window.innerWidth / 2 : 960;
  case 'bottom-left':
    return position.x;
  case 'bottom-right':
    return typeof window !== 'undefined' 
      ? window.innerWidth - position.offset 
      : 1920 - position.offset;
  case 'top-right':
    return typeof window !== 'undefined' 
      ? window.innerWidth - position.x 
      : 1920 - position.x;
  default:
    return 0;
  }
}

/**
 * Calculate actual Y position from strategy
 */
export function calculateY(position: PanelPosition): number {
  switch (position.type) {
  case 'absolute':
    return position.y;
  case 'center':
    return position.y;
  case 'bottom-left':
    return typeof window !== 'undefined' 
      ? window.innerHeight - position.offset 
      : 1080 - position.offset;
  case 'bottom-right':
    return position.y !== undefined 
      ? position.y 
      : (typeof window !== 'undefined' ? window.innerHeight - 20 : 1060);
  case 'top-right':
    return position.y;
  default:
    return 0;
  }
}

/**
 * Default panel configurations
 */
export const HUD_PANEL_DEFAULTS = {
  /** Player info panel - top left, below debug toolbox */
  playerInfo: {
    position: { type: 'absolute' as const, x: 20, y: 60 },
    width: 300,
    minWidth: 250,
    resizable: false,
  },

  /** Action bar - centered at bottom */
  actionBar: {
    position: { type: 'center' as const, y: 0 }, // Y calculated from bottom
    bottomOffset: 100,
    width: 400,
    height: 80,
    resizable: false,
  },

  /** Chat panel - bottom right, above action bar */
  chat: {
    position: { type: 'bottom-right' as const, offset: 280, y: 520 },
    width: 400,
    height: 450,
    minWidth: 350,
    minHeight: 300,
    resizable: true,
  },

  /** System panels (job/stats) - top right, below Online Players toggle */
  systemPanels: {
    position: { type: 'top-right' as const, x: 20, y: 80 },
    width: 200,
    minWidth: 180,
    resizable: false,
  },

  /** Quick menu - bottom right, left of viewport button */
  quickMenu: {
    position: { type: 'bottom-right' as const, offset: 24, y: 16 },
    /**
     * Expanded panel border-box width (QuickMenu.tsx shellStyle; mirrored by
     * WindowLauncher.QUICK_MENU_PANEL_WIDTH, which derives the 🛠 cluster's
     * horizontal clearance from the base right inset). W2a-2 (2026-09-28) makes
     * it the compact clamp ceiling as well.
     */
    width: 196,
  },

  /** Debug toolbox — LEGACY SHAPE, value unchanged (U1, 2026-09-28). The
   *  rendered anchor is derived in src/game/ui/framework/WindowLauncher.tsx
   *  (DEBUG_TOOLBOX_*: bottom-right cluster, one gap above the 🛠 launcher
   *  button) and applied by HUD.styles.ts `debugToolboxContainerStyle`. No
   *  consumer reads this entry (grep-verified 2026-09-28); it is kept so the
   *  HUD_PANEL_DEFAULTS shape stays stable. */
  debugToolbox: {
    position: { type: 'absolute' as const, x: 10, y: 10 },
  },

  /** Map banner - centered at top */
  mapBanner: {
    position: { type: 'center' as const, y: 10 },
  },
} as const;

/**
 * Compact landscape HUD (W2a, 2026-09-28) — phone-landscape / short windows.
 *
 * DJ's U01 review: at 844×390 the left-column target frame sat on the chat
 * panel's upper rows and the EXPANDED quick-menu panel sat on the action-bar
 * band. The compact treatment moves the bottom-half surfaces into a documented
 * band stack instead of letting each dock grow into the others:
 *
 *   top:      player unit frame (left, unchanged) + map banner (center)
 *   left col: target frame, below the unit frame, capped above the action band
 *   right:    quick menu (top, below the banner clearance) → chat peek → void
 *   bottom:   scaled action-bar band (centered)
 *
 * Derivation (all px; see hudCompactLayout.ts for the composed runtime values):
 *   actionBarBandHeight = round(ACTION_BAR_NATURAL_HEIGHT × scale)  → 90×0.75 = 68
 *   chatBottomInset     = VIEWPORT_MARGIN + bandHeight + ELEMENT_GAP → 16+68+10 = 94
 *   chatWidth           = clamp(viewport − rightInset − left column − gap,
 *                               minWidth, maxWidth)  → 298 @640 / 380 @844
 *   quickMenuBottomInset= chatHeight + chatBottomInset + ELEMENT_GAP → 104+94+10 = 208
 *   quickMenuMaxHeight  = quickMenuBottomY − quickMenuTop
 *   quickMenuTop        = max(banner clearance, measured minimap bottom + gap)
 *                         when that slot stays usable; otherwise the banner
 *                         clearance with the panel anchored LEFT of the
 *                         measured minimap (W2a-2, see `quickMenu` below)
 * The action-bar band scales (dot 0.75) because the natural 10-slot row is
 * 652 px wide — wider than a 640 px viewport. Scaling is visual only: the
 * gameplay slot geometry (58×58, ActionBarStyles.test.tsx) is untouched.
 */
export const HUD_COMPACT_LAYOUT = {
  /**
   * Height at/below which the compact landscape HUD applies. Aliased by
   * `HUD_TOP_CONSTANTS.COMPACT_HEIGHT_THRESHOLD` (hudTopLayout.ts) so the
   * target-panel clamp and the HUD bands switch at the same breakpoint.
   */
  maxViewportHeight: 450,
  actionBar: {
    /**
     * Scale about the band's bottom-centre. 652 × 0.75 = 489 px leaves a 640 px
     * viewport ≥ 75 px of side margin on each edge.
     */
    scale: 0.75,
  },
  chat: {
    /**
     * Peek height — channel-tab row + input row + ~1 message line. Chat stays
     * fully functional (Enter-to-focus input is always visible); older rows
     * scroll inside the peek instead of pushing the dock up the viewport.
     */
    height: 104,
    /** Minimum usable peek (tabs + input + one message line). */
    minWidth: 280,
    /** Right inset of the compact peek (mirrors the HUD VIEWPORT_MARGIN). */
    rightInset: 16,
    /** Cap on the peek width — the desktop chat surface width. */
    maxWidth: 380, // HUD_PANEL_DEFAULTS.chat.width
  },
  /**
   * Compact quick menu (W2a-2, 2026-09-28). W2a anchored the expanded panel at
   * the banner clearance inside the minimap's vertical band, so at 844×390 its
   * upper rows sat behind the top-right minimap (624,76→820,182 vs minimap
   * 652,10→834,152). The anchor now derives from the MEASURED minimap rect
   * (hudCompactLayout.ts / useHudMinimapRect):
   *
   *  - preferred: top = max(banner clearance, minimap.bottom + GAP) when the
   *    slot still fits `minUsableHeight`;
   *  - otherwise the panel keeps the banner clearance and slides LEFT of the
   *    measured minimap (right edge = minimap.left − GAP), width clamped to the
   *    free column between the visual left HUD column and the minimap.
   *
   * The below-minimap slot is only 20 px at 844×390 and negative at 640×360
   * (chat peek bottom − minimap bottom), which is why the fallback branch is
   * the one exercised at the required short viewports.
   */
  quickMenu: {
    /**
     * Minimum usable expanded-panel height for the preferred below-minimap
     * anchor: QuickMenu header ≈ 23 px (10 px label + 2/4 px padding + 1 px
     * rule + 4 px gap) + two entry rows ≈ 24 px each → 71 → 72.
     */
    minUsableHeight: 72,
    /**
     * Width floor for the left-of-minimap anchor. Below this the panel could
     * not show a label next to its icon, so the clamp stops here (landscape
     * viewports narrower than ~618 px are best-effort, matching W2a's
     * documented chat-peek clamp caveat).
     */
    minWidth: 140,
    /**
     * Fallback minimap geometry for the left/below anchor when the live
     * `[data-hud-surface="minimap"]` rect is absent or unmeasurable (jsdom,
     * first paint): mirrors Minimap.tsx's shipped defaults — fixed top/right
     * 10 px, 180×140 canvas + 1 px brass frame per side.
     */
    minimapFallback: {
      top: 10,
      right: 10,
      width: 180,
      height: 140,
      /** Brass frame per side (the canvas border) — 2 px total each axis. */
      frame: 1,
    },
  },
} as const;

/**
 * Default colors and styling
 */
export const HUD_COLORS = {
  background: {
    default: 'rgba(0, 0, 0, 0.6)',
    dark: 'rgba(0, 0, 0, 0.8)',
    light: 'rgba(0, 0, 0, 0.3)',
    transparent: 'rgba(0, 0, 0, 0.5)',
  },
  border: {
    default: '1px solid rgba(255, 255, 255, 0.2)',
    light: '1px solid rgba(255, 255, 255, 0.1)',
    error: '2px solid #e74c3c',
  },
  text: {
    primary: '#ffffff',
    secondary: 'rgba(255, 255, 255, 0.7)',
    muted: 'rgba(255, 255, 255, 0.5)',
    accent: '#ffd700', // Gold for map name
    error: '#e74c3c',
    warning: '#ff4757',
  },
  resource: {
    health: '#ff4757',
    mana: '#3498db',
    xp: '#ffd700',
  },
} as const;

/**
 * Default typography
 */
export const HUD_TYPOGRAPHY = {
  fontSize: {
    xs: '10px',
    sm: '11px',
    md: '12px',
    lg: '14px',
    xl: '16px',
    xxl: '18px',
    title: '24px',
  },
  fontWeight: {
    normal: 'normal',
    medium: '500',
    bold: 'bold',
  },
  letterSpacing: {
    normal: 'normal',
    wide: '1px',
  },
} as const;

/**
 * Z-index layers for HUD elements
 */
export const HUD_Z_INDEX = {
  background: 0,
  panels: 100,
  actionbar: 150,
  draggable: 200,
  tooltip: 300,
  modal: 400,
  debug: 1000,
  connectionStatus: 9999,
} as const;

/**
 * Responsive breakpoints
 */
export const HUD_BREAKPOINTS = {
  mobile: 768,
  tablet: 1024,
  desktop: 1440,
} as const;

/**
 * Get responsive panel width based on screen size
 */
export function getResponsiveWidth(
  defaultWidth: number,
  _breakpoint: 'mobile' | 'tablet' | 'desktop' = 'desktop'
): number {
  if (typeof window === 'undefined') return defaultWidth;
  
  const width = window.innerWidth;
  
  if (width < HUD_BREAKPOINTS.mobile) {
    return Math.min(defaultWidth, width - 40);
  }
  if (width < HUD_BREAKPOINTS.tablet) {
    return Math.min(defaultWidth, width - 60);
  }
  
  return defaultWidth;
}

/**
 * Calculate centered X position with offset
 */
export function getCenteredX(offset: number = 0): number {
  if (typeof window === 'undefined') return 960 + offset;
  return window.innerWidth / 2 + offset;
}

/**
 * Calculate position from bottom edge
 */
export function getFromBottom(offset: number): number {
  if (typeof window === 'undefined') return 1080 - offset;
  return window.innerHeight - offset;
}
